import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FooterSlotContext = createContext<HTMLDivElement | null>(null);

/** Wraps the wizard's scrollable body + a pinned footer bar. Every step's
 *  `<WizardFooter>` portals into that bar instead of scrolling away with the
 *  step's own content — see `WizardFooter` below. */
export function ImportWizardFooterProvider({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Created synchronously on first render (not via a ref callback) so the
  // very first paint already has a real portal target — a ref-callback-driven
  // slot would only exist one render late, flashing each step's footer
  // inline before it jumps into the pinned bar on step mount.
  const [slot] = useState(() => {
    const el = document.createElement('div');
    el.className = 'w-full';
    return el;
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.appendChild(slot);
    return () => { container.removeChild(slot); };
  }, [slot]);

  return (
    <FooterSlotContext.Provider value={slot}>
      {children}
      <div ref={containerRef} className="flex shrink-0 items-center border-t border-border px-6 py-4" />
    </FooterSlotContext.Provider>
  );
}

/** A step's bottom action row (Continue/Upload/Commit button, row counts,
 *  download-template link, etc.). Portals into the wizard's pinned footer bar
 *  when rendered inside `ImportWizardFooterProvider`; falls back to rendering
 *  inline when a step is unit-tested standalone (no provider ancestor). */
export function WizardFooter({ children }: { children: ReactNode }) {
  const slot = useContext(FooterSlotContext);
  if (!slot) return <>{children}</>;
  return createPortal(children, slot);
}
